import {
  Commitment,
  nextCycleDate,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CommonSubmitButton } from "@/shared/components/button-common";
import {
  ListCommitmentsRequest,
  useRelationshipsRelationshipCreateSeriesMutation,
} from "@/services/backend/relationships/api/service";
import {
  CommitmentsTable,
  CommitmentsTableFilters,
} from "@/services/backend/relationships/components/commitments-table";
import { ReactNode, useCallback, useState } from "react";
import { NavLink } from "react-router";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { DeleteSeriesDialog } from "@/routes/customers/[customerId]/relationships/[relationshipId]/commitments/[commitmentId]/_components/delete-series-dialog";
import { RePopulateSeriesDialog } from "@/routes/customers/[customerId]/relationships/[relationshipId]/commitments/[commitmentId]/_components/re-populate-series-dialog";
import { DateInput } from "@/shared/components/form/date-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";

export function SeriesCard({
  relationship,
  commitment,
}: {
  relationship: Relationship;
  commitment: Commitment;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
          <CardTitle>{t("Serie")}</CardTitle>
          {commitment.seriesId && (
            <div className="flex h-3 gap-2">
              <RePopulateSeriesDialog
                relationshipId={relationship.id}
                commitment={commitment}
              />
              <DeleteSeriesDialog
                relationshipId={relationship.id}
                seriesId={commitment.seriesId}
              />
            </div>
          )}
        </div>
        <CardDescription>
          {t(
            "Informationen zur Serie in welcher die Sollstellung ausgeführt wird.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {commitment.seriesId === null ? (
          <NoSeriesPage relationship={relationship} commitment={commitment} />
        ) : (
          <SeriesPage relationship={relationship} commitment={commitment} />
        )}
      </CardContent>
    </Card>
  );
}

function NoSeriesPage({
  relationship,
  commitment,
}: {
  relationship: Relationship;
  commitment: Commitment;
}) {
  const [request, setRequest] = useState({
    id: relationship.id,
    commitmentId: commitment.id,
    firstDate: nextCycleDate(commitment),
  });
  const [post, { isLoading, error, reset }] =
    useRelationshipsRelationshipCreateSeriesMutation();

  const doCreate = () => {
    if (isLoading) {
      return;
    }

    post(request);
  };

  return (
    <div className="grid gap-4">
      <Alert>
        <AlertTriangle />
        <AlertTitle>{t("Keine Serie")}</AlertTitle>
        <AlertDescription>
          {t(
            `Die Sollstellung gehört noch keiner Serie an. Ein Klick auf "Serie erstellen" erstellt im Turnus von ${commitment.cycle} Wochen Sollstellungen bis zum Ende der Beziehung. Basis für die Serie ist diese Sollstellung. Diese wird auch Teil der Serie.`,
          )}
        </AlertDescription>
      </Alert>
      <RTKQueryErrorAlert error={error} onDismiss={() => reset()} />
      <div className="grid gap-4">
        <DateInput
          label={t("Erstes Datum")}
          date={request.firstDate}
          onDateChange={(firstDate) =>
            setRequest({ ...request, firstDate: firstDate ?? "" })
          }
          errors={inputValidationErrorMessages(error, "firstDate")}
          hint={t(
            "Standardmäßig ist das erste Datum ein Turnus nach dem Datum dieser Sollstellung. Wenn ein anders Datum passender ist, kann das hier angegeben werden. Das Datum dieser Sollstellung wird nicht geändert.",
          )}
        />
        <CommonSubmitButton isLoading={isLoading} onClick={doCreate}>
          {t("Serie erstellen")}
        </CommonSubmitButton>
      </div>
    </div>
  );
}

function SeriesPage({
  relationship,
  commitment,
}: {
  relationship: Relationship;
  commitment: Commitment;
}) {
  const [request, setRequest] = useState<ListCommitmentsRequest>({
    relationship: {
      active: true,
      values: [relationship.id],
    },
    series: {
      active: true,
      values: [commitment.seriesId ?? NullUUID],
    },
    limit: 10,
  });
  const [selection, setSelection] = useState<Commitment[]>([]);

  const renderCommitmentLink = useCallback(
    (c: Commitment, rel: Relationship, children: ReactNode) => (
      <NavLink
        to={`/customers/${rel.customerId}/relationships/${c.relationshipId}/commitments/${c.id}`}
      >
        {children}
      </NavLink>
    ),
    [],
  );

  return (
    <div className="grid gap-2">
      <CommitmentsTableFilters
        request={request}
        onRequestChange={setRequest}
        selection={selection}
        onSelectionChange={setSelection}
      />
      <CommitmentsTable
        request={request}
        onRequestChange={setRequest}
        selection={selection}
        onSelectionChange={setSelection}
        highlightAsCurrent={commitment.id}
        skipLoading={commitment.seriesId === null}
        renderCommitmentLink={renderCommitmentLink}
        renderDocumentLink={(path, children) => (
          <NavLink to={path}>{children}</NavLink>
        )}
      />
    </div>
  );
}
